import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, firstValueFrom, of } from 'rxjs';

import { API_CONFIG } from '../config/api.config';
import type { Locale } from '../i18n/translation.model';
import { AuthService } from './auth.service';
import { UserService } from './user.service';

export const SUPPORTED_LOCALES: Locale[] = ['es', 'en'];
const DEFAULT_LOCALE: Locale = 'en';
const LOCALE_STORAGE_KEY = 'hirefit-locale';

const INTL_LOCALES: Record<Locale, string> = { es: 'es-AR', en: 'en-US' };

@Injectable({ providedIn: 'root' })
export class LocaleService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);

  private readonly localeState = signal<Locale>(DEFAULT_LOCALE);
  readonly locale = this.localeState.asReadonly();

  /** BCP-47 tag for Intl/toLocaleDateString formatting, derived from the active UI locale. */
  readonly intlLocale = computed(() => INTL_LOCALES[this.localeState()]);

  /**
   * Resolves the active locale once at app startup: user.preferredLanguage (if a session
   * exists) → localStorage → default 'en'. Wired via provideAppInitializer in app.config.ts.
   */
  async init(): Promise<void> {
    const storedLocale = this.readStoredLocale();

    if (this.authService.auth()) {
      const user = await firstValueFrom(
        this.userService.getMe().pipe(catchError(() => of(null))),
      );
      const preferred = this.toSupportedLocale(user?.preferredLanguage);
      this.localeState.set(preferred ?? storedLocale ?? DEFAULT_LOCALE);
      return;
    }

    this.localeState.set(storedLocale ?? DEFAULT_LOCALE);
  }

  setLocale(locale: Locale): void {
    this.localeState.set(locale);
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);

    const user = this.authService.auth()?.user;
    if (!user) {
      return;
    }

    // Persist preferredLanguage only — backend accepts partial UserUpdateRequest.
    this.http
      .put(`${API_CONFIG.baseUrl}/users/${user.id}`, {
        preferredLanguage: locale.toUpperCase(),
      })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  private readStoredLocale(): Locale | null {
    try {
      return this.toSupportedLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
    } catch {
      return null;
    }
  }

  private toSupportedLocale(value: string | null | undefined): Locale | null {
    if (!value) {
      return null;
    }
    const normalized = value.toLowerCase() as Locale;
    return SUPPORTED_LOCALES.includes(normalized) ? normalized : null;
  }
}
